import { ModalData } from '../models/modal'
import { GBModal } from '../components/core/modal/modal'

export class GBModalService {
	public static setupModalOpenListener(): void {
		document.body.addEventListener('click', e => {
			let target = e.target as HTMLElement

			let targetModalId = target.getAttribute('data-gb-modal-id')

			if (!targetModalId) {
				target = target.closest('[data-gb-modal-id]') as HTMLElement

				if (target) {
					targetModalId = target.getAttribute('data-gb-modal-id')
				}
			}

			if (targetModalId) {
				e.preventDefault()
				GBModalService.openModal({
					modalId: targetModalId,
					title: target.getAttribute('data-gb-modal-title') || '',
					titleCustomColor: target.getAttribute('data-gb-modal-title-custom-color') || '',
					contentPath: target.getAttribute('data-gb-modal-content-path'),
					isFullscreen:
						target.hasAttribute('data-gb-modal-is-legacy-gallery') ||
						target.hasAttribute('data-gb-adv-scroller-slide-num') ||
						target.hasAttribute('data-gb-modal-is-gallery')
							? true
							: target.hasAttribute('data-gb-modal-is-fullscreen'),
					isDialog: target.hasAttribute('data-gb-modal-is-dialog'),
					isLeftAligned: target.hasAttribute('data-gb-modal-is-left-aligned'),
					isRightAligned: target.hasAttribute('data-gb-modal-is-right-aligned'),
					isBottomAligned: target.hasAttribute('data-gb-modal-is-bottom-aligned'),
					anchor: target.getAttribute('data-gb-modal-anchor') || null,
					isLegacyGallery: target.hasAttribute('data-gb-modal-is-legacy-gallery'),
					isNewGallery: target.hasAttribute('data-gb-modal-is-gallery'),
					scrollToSlide: target.hasAttribute('data-gb-modal-is-legacy-gallery')
						? parseInt(target.getAttribute('data-slide-scrollto'), 10)
						: 0,
					scrollToAdvScrlSlide: target.hasAttribute('data-gb-adv-scroller-slide-num')
						? parseInt(target.getAttribute('data-gb-adv-scroller-slide-num'), 10)
						: null,
					modalWidth: target.hasAttribute('data-gb-modal-width')
						? target.getAttribute('data-gb-modal-width')
						: null,
					modalHeight: target.hasAttribute('data-gb-modal-height')
						? target.getAttribute('data-gb-modal-height')
						: null,
					slideAnimation: target.getAttribute('data-gb-modal-slide-animation') || null,
					isLazyLoad: target.hasAttribute('data-gb-modal-is-lazy-loading'),
				} as ModalData)
			}
		})
	}

	/**
	 * Creates the gb-modal element and calls the modal component's openModal() method
	 *
	 * @param modalData {modalData}- Info to support the proper display of the modal
	 */
	public static openModal(modalData: ModalData): void {
		const preexistingModalEl = document.querySelector(`gb-modal#${modalData.modalId}`) as GBModal

		if (preexistingModalEl) {
			// Reset modal state to handle secondary triggers
			if (preexistingModalEl.hasAttribute('data-scroll-to-slide')) {
				preexistingModalEl.removeAttribute('data-scroll-to-slide')
			}
			if (preexistingModalEl.hasAttribute('data-gb-modal-is-legacy-gallery')) {
				preexistingModalEl.removeAttribute('data-gb-modal-is-legacy-gallery')
			}
			if (preexistingModalEl.hasAttribute('data-gb-modal-is-gallery')) {
				preexistingModalEl.removeAttribute('data-gb-modal-is-gallery')
			}
			if (preexistingModalEl.hasAttribute('data-gb-modal-is-fullscreen')) {
				preexistingModalEl.removeAttribute('data-gb-modal-is-fullscreen')
			}

			// Reapply attributes based on the new modalData
			if (!isNaN(modalData.scrollToSlide) && !preexistingModalEl.hasAttribute('data-scroll-to-slide')) {
				preexistingModalEl.setAttribute('data-scroll-to-slide', modalData.scrollToSlide.toString())
			}
			if (modalData.isLegacyGallery && !preexistingModalEl.hasAttribute('data-gb-modal-is-legacy-gallery')) {
				preexistingModalEl.setAttribute('data-gb-modal-is-legacy-gallery', '')
			}
			if (modalData.isNewGallery && !preexistingModalEl.hasAttribute('data-gb-modal-is-gallery')) {
				preexistingModalEl.setAttribute('data-gb-modal-is-gallery', '')
			}
			if (modalData.isFullscreen && !preexistingModalEl.hasAttribute('data-gb-modal-is-fullscreen')) {
				preexistingModalEl.setAttribute('data-gb-modal-is-fullscreen', '')
			}

			preexistingModalEl.openModal(modalData)
		} else {
			const newModalEl = document.createElement('gb-modal') as GBModal
			newModalEl.setAttribute('id', modalData.modalId)

			// To support Quantum's legacy gallery
			if (!isNaN(modalData.scrollToSlide)) {
				newModalEl.setAttribute('data-scroll-to-slide', modalData.scrollToSlide.toString())
			}
			if (modalData.isLegacyGallery) {
				newModalEl.setAttribute('data-gb-modal-is-legacy-gallery', '')
			}
			if (modalData.isNewGallery) {
				newModalEl.setAttribute('data-gb-modal-is-gallery', '')
			}
			if (modalData.isFullscreen) {
				newModalEl.setAttribute('data-gb-modal-is-fullscreen', '')
			}

			document.body.appendChild(newModalEl)
			newModalEl.openModal(modalData)
		}
	}

	public static closeModal(modalId: string): void {
		const modalEl = document.querySelector(`gb-modal#${modalId}`) as GBModal

		if (modalEl) {
			modalEl.hideModal()
		} else {
			window.console.log(`ERROR: Cannot close modal with id ${modalId} becaused it was not found`)
		}
	}

	public static setupExternalLinkListener(): void {
		document.body.addEventListener(
			'click',
			(e: MouseEvent) => {
				let el = e.target as HTMLElement | null
				while (el) {
					if (el.nodeName === 'A' && (el as HTMLAnchorElement).target === '_blank') {
						const url: string = (el as HTMLAnchorElement).href
						if (!url || typeof url !== 'string') {
							window.console.log('Error: Invalid or empty URL found on link element.')
							break
						}
						const linkTypeEl = el.closest('[data-link-type]') as HTMLElement | null
						let linkType: string = ''
						if (linkTypeEl) {
							linkType = linkTypeEl.getAttribute('data-link-type')
						}
						const isExternal: boolean = linkType === 'external'
						const popupEnabled = !!document.querySelector('gb-modal#external-popup-content-reveal')
						const bypassPopup = el.getAttribute('data-displaypopup') === 'false' || url.endsWith('#')
						if (isExternal && popupEnabled && !bypassPopup) {
							e.preventDefault()
							GBModalService.onExternalSiteModal(url)
						}
						break
					}
					el = el.parentNode as HTMLElement | null
				}
			},
			true
		)
	}

	public static onExternalSiteModal(url: string): void {
		const modalId = 'external-popup-content-reveal'
		const externalSiteModalEl = document.querySelector(`gb-modal#${modalId}`) as GBModal
		if (!externalSiteModalEl) {
			console.error('External site modal element not found.')
			return
		}
		GBModalService.openModal({
			modalId: modalId,
		})
		const continueBtnEl = externalSiteModalEl.querySelector('.gb-popup-cont-button') as HTMLAnchorElement
		const cancelBtnEl = externalSiteModalEl.querySelector('.gb-popup-cancel-button') as HTMLElement
		if (!continueBtnEl || !cancelBtnEl) {
			console.error('Continue or cancel button not found in external site modal.')
			return
		}
		if (!continueBtnEl.getAttribute('listener-attached')) {
			const handleContinueClick = (e: MouseEvent) => {
				e.preventDefault()
				const urlWithHash = url.endsWith('/') ? `${url.slice(0, -1)}#` : `${url}#`
				window.open(urlWithHash, '_blank')
				GBModalService.closeModal(modalId)
				continueBtnEl.removeEventListener('click', handleContinueClick)
				continueBtnEl.removeAttribute('listener-attached')
			}
			continueBtnEl.addEventListener('click', handleContinueClick)
			continueBtnEl.setAttribute('listener-attached', 'true')
		}
		if (!cancelBtnEl.getAttribute('listener-attached')) {
			const handleCloseClick = (e: MouseEvent) => {
				e.preventDefault()
				GBModalService.closeModal(modalId)
				cancelBtnEl.removeEventListener('click', handleCloseClick)
				cancelBtnEl.removeAttribute('listener-attached')
			}
			cancelBtnEl.addEventListener('click', handleCloseClick)
			cancelBtnEl.setAttribute('listener-attached', 'true')
		}
	}
}
